import React, { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { User, Building2, CheckCircle, X, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { safeArray, safeString } from '@/components/safeRender';
import { formatCurrency } from '@/lib/formatters';
import { effectiveTituloStatus } from '@/lib/utils';
import {
  cardSectionBorder,
  gestorBadgeAccepted,
  gestorBadgeDenied,
  operatorAvatar,
  operatorAvatarEmpty,
  operatorAvatarIcon,
  statBoxBlue,
  statBoxBlueCaption,
  statBoxBlueLabel,
  statBoxOrange,
  statBoxOrangeLabel,
  statBoxOrangeValue,
  statBoxRed,
  statBoxRedCaption,
  statBoxRedLabel,
  statBoxTotal,
} from '@/lib/uiClasses';
import { calcularDiasAtraso, getGestorActionStatus } from './kanbanCardUtils';

export interface KanbanSortableCardProps {
  card: any;
  isSelected?: boolean;
  onSelect?: (cardId: string, selected: boolean) => void;
  onCardClick?: (card: any) => void;
}

const SortableCardInner: React.FC<KanbanSortableCardProps> = ({
  card,
  isSelected = false,
  onSelect,
  onCardClick,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const cliente = card.cliente;
  const titulos = safeArray(card.titulos);
  const gestorStatus = getGestorActionStatus(card);

  const getStatusLocacao = () => {
    if (!titulos || titulos.length === 0) return null;

    const statusCounts = new Map<string, number>();
    titulos.forEach((titulo: any) => {
      if (titulo.statusLocacao) {
        const normalizedStatus = titulo.statusLocacao === 'APROVADA' ? 'ATIVA' : titulo.statusLocacao;
        statusCounts.set(normalizedStatus, (statusCounts.get(normalizedStatus) || 0) + 1);
      }
    });

    if (statusCounts.size === 0) return null;

    let mostCommonStatus = '';
    let maxCount = 0;
    statusCounts.forEach((count, status) => {
      if (count > maxCount) {
        maxCount = count;
        mostCommonStatus = status;
      }
    });

    return mostCommonStatus;
  };

  const statusLocacao = getStatusLocacao();

  const companyName =
    cliente?.empresa?.nome ??
    cliente?.company?.name ??
    (titulos as Array<{ company?: { name?: string } }>).find((t) => t?.company?.name)?.company?.name ??
    null;

  const titulosVencidos = titulos.filter(
    (t: any) => effectiveTituloStatus(t.status, t.vencimento) === 'VENCIDO' || t.status === 'PARCIAL'
  );
  const titulosParciais = titulos.filter((t: any) => t.status === 'PARCIAL');

  const valorTotal = titulos.reduce((sum: number, t: any) => sum + Number(t.saldo || t.valor), 0);
  const valorVencido = titulosVencidos.reduce((sum: number, t: any) => sum + Number(t.saldo || t.valor), 0);
  const valorParciais = titulosParciais.reduce((sum: number, t: any) => sum + Number(t.saldo || t.valor), 0);
  const valorVencidoTotal = valorVencido + valorParciais;
  const diasAtraso = calcularDiasAtraso(titulos as Array<{ status?: string; vencimento?: string | Date }>);

  const operadoresAlocados = titulos
    .map((t: any) => t.operador)
    .filter((op: any) => op && op.id)
    .reduce((acc: any[], op: any) => {
      if (!acc.find((existing: any) => existing.id === op.id)) {
        acc.push(op);
      }
      return acc;
    }, []);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
          bg-card text-card-foreground rounded-lg border shadow-sm
          hover:shadow-md dark:hover:shadow-lg dark:hover:shadow-slate-900/50
          transition-all duration-200
          cursor-grab active:cursor-grabbing
          ${isSelected ? 'ring-2 ring-green-500 border-green-500' : ''}
          ${gestorStatus === 'ACEITO' ? 'border-green-400 dark:border-green-500 hover:border-green-500 dark:hover:border-green-400' : ''}
          ${gestorStatus === 'NEGADO' ? 'border-red-400 dark:border-red-500 hover:border-red-500 dark:hover:border-red-400' : ''}
          ${!gestorStatus && !isSelected ? 'border-border hover:border-muted-foreground/30' : ''}
          ${isDragging ? 'shadow-lg dark:shadow-xl dark:shadow-slate-900/70 rotate-2 scale-105' : ''}
        `}
      onClick={() => onCardClick?.(card)}
    >
      <div className={`p-3 ${cardSectionBorder}`}>
        {(gestorStatus === 'ACEITO' || gestorStatus === 'NEGADO') && (
          <div className="mb-2 flex justify-center">
            {gestorStatus === 'ACEITO' && (
              <Badge className={gestorBadgeAccepted}>
                <CheckCircle className="w-3 h-3 mr-1" />
                Aceito pelo Gestor
              </Badge>
            )}
            {gestorStatus === 'NEGADO' && (
              <Badge className={gestorBadgeDenied}>
                <X className="w-3 h-3 mr-1" />
                Negado pelo Gestor
              </Badge>
            )}
          </div>
        )}

        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {onSelect && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(card.id, !isSelected);
                }}
                className="cursor-pointer"
              >
                <Checkbox checked={isSelected} onChange={() => {}} />
              </div>
            )}
            <div className={operatorAvatar}>
              <User className={operatorAvatarIcon} />
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold text-foreground truncate">
                {safeString(cliente?.nome, 'Cliente sem nome')}
              </h4>
              {cliente?.codigo && (
                <p className="text-xs text-muted-foreground font-mono">{cliente.codigo}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0">
            <Badge variant="outline" className="text-xs">
              {titulos.length}
            </Badge>
          </div>
        </div>

        {operadoresAlocados.length > 0 ? (
          <div className="mt-2 flex items-center space-x-2">
            <div className={operatorAvatar}>
              <User className={operatorAvatarIcon} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">
                Operador: {operadoresAlocados[0].name}
              </p>
              {operadoresAlocados.length > 1 && (
                <p className="text-xs text-muted-foreground/80">
                  +{operadoresAlocados.length - 1} outros
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-2 flex items-center space-x-2">
            <div className={operatorAvatarEmpty}>
              <User className="w-3 h-3 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Sem operador alocado</p>
            </div>
          </div>
        )}

        {(companyName || statusLocacao) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {companyName && (
              <Badge className="text-xs bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600">
                <Building2 className="w-3 h-3 mr-1" />
                <span className="truncate max-w-[180px]">{companyName}</span>
              </Badge>
            )}
            {statusLocacao && (
              <Badge
                className={`
                    text-xs
                    ${statusLocacao === 'ATIVA' ? 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-200' : ''}
                    ${statusLocacao === 'FINALIZADA' ? 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-200' : ''}
                    ${statusLocacao === 'CANCELADA' ? 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200' : ''}
                    ${!['ATIVA', 'FINALIZADA', 'CANCELADA'].includes(statusLocacao) ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-200' : ''}
                  `}
              >
                {statusLocacao}
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div className={statBoxBlue}>
            <div className={statBoxBlueLabel}>{titulosVencidos.length + titulosParciais.length}</div>
            <div className={statBoxBlueCaption}>Em Atraso</div>
          </div>
          <div className={statBoxRed}>
            <div className={statBoxRedLabel}>{formatCurrency(valorVencidoTotal)}</div>
            <div className={statBoxRedCaption}>Valor Vencido</div>
          </div>
        </div>

        {diasAtraso > 0 && (
          <div className={statBoxOrange}>
            <div className="flex items-center space-x-2">
              <Calendar className="w-3 h-3 text-orange-600 dark:text-orange-400" />
              <span className={statBoxOrangeLabel}>Dias em Atraso</span>
            </div>
            <span className={statBoxOrangeValue}>
              {diasAtraso} dia{diasAtraso !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        <div className={statBoxTotal}>
          <div className="text-sm font-bold text-foreground">{formatCurrency(valorTotal)}</div>
          <div className="text-xs text-muted-foreground">Total Geral</div>
        </div>
      </div>
    </div>
  );
};

function cardsEqual(prev: KanbanSortableCardProps, next: KanbanSortableCardProps): boolean {
  return (
    prev.card.id === next.card.id &&
    prev.isSelected === next.isSelected &&
    prev.card.gestorActionStatus === next.card.gestorActionStatus &&
    prev.onSelect === next.onSelect &&
    prev.onCardClick === next.onCardClick
  );
}

export const SortableCard = memo(SortableCardInner, cardsEqual);
